import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  getClients, 
  getServices, 
  getArticles,
  getContentTemplates,
  getInvoiceTemplates,
  createInvoice, 
  getInvoice, 
  updateInvoice,
  emitInvoice,
} from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, 
  Plus, 
  Trash2,
  Save,
  FileText,
  Palette,
  Lock,
  Send,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Client360Link } from '../../components/ui/client-360-link';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: 'always', style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

export const InvoiceFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;

  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [contentTemplates, setContentTemplates] = useState([]);
  const [designTemplates, setDesignTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    client_id: searchParams.get('client_id') || '',
    invoice_number: '',
    series: 'FAC',
    issue_date: new Date().toISOString().split('T')[0],
    operation_date: '',
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 21 }],
    notes: '',
    status: 'draft',
    irpf_rate: 0,
    auto_stock: true,
    template_id: ''
  });
  const [isLocked, setIsLocked] = useState(false);
  const [fieldErrors, setFieldErrors] = useState([]); // [{field, code, message}]
  const [emitting, setEmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clientsRes, servicesRes, articlesRes, contentRes, designRes] = await Promise.all([
        getClients().catch(() => ({ data: [] })),
        getServices().catch(() => ({ data: [] })),
        getArticles().catch(() => ({ data: [] })),
        getContentTemplates().catch(() => ({ data: [] })),
        getInvoiceTemplates().catch(() => ({ data: [] }))
      ]);
      setClients(clientsRes.data || []);
      setServices(servicesRes.data || []);
      setArticles(articlesRes.data || []);
      setContentTemplates(contentRes.data || []);
      setDesignTemplates(designRes.data || []);

      if (isEditing) {
        const { data } = await getInvoice(id);
        setFormData({
          client_id: data.client_id || '',
          invoice_number: data.invoice_number || '',
          series: data.series || 'FAC',
          issue_date: data.issue_date || new Date().toISOString().split('T')[0],
          operation_date: data.operation_date || '',
          due_date: data.due_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          items: data.items && data.items.length > 0 ? data.items : [{ description: '', quantity: 1, unit_price: 0, tax_rate: 21 }],
          notes: data.notes || '',
          status: data.status || 'draft',
          irpf_rate: data.irpf_rate || 0,
        });
        setIsLocked((data.status || 'draft') !== 'draft');
      }
    } catch (e) {
      console.error('Error loading invoice form data:', e);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, tax_rate: 21 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addServiceToItems = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setFormData({
        ...formData,
        items: [
          ...formData.items.filter(item => item.description),
          {
            service_id: service.id,
            description: service.name,
            quantity: 1,
            unit_price: service.hourly_rate,
            tax_rate: 21
          }
        ]
      });
    }
  };

  const addArticleToItems = (articleId) => {
    const article = articles.find(a => a.id === articleId);
    if (article) {
      setFormData({
        ...formData,
        items: [
          ...formData.items.filter(item => item.description),
          {
            article_id: article.id,
            description: article.sku ? `${article.name} (${article.sku})` : article.name,
            quantity: 1,
            unit_price: article.unit_price || 0,
            tax_rate: article.tax_rate ?? 21
          }
        ]
      });
    }
  };

  const applyContentTemplate = (tplId) => {
    const tpl = contentTemplates.find(t => t.id === tplId);
    if (!tpl) return;
    const dueDate = new Date(Date.now() + (tpl.default_due_days || 30) * 24 * 60 * 60 * 1000)
      .toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      items: (tpl.items || []).map(it => ({ ...it })),
      notes: tpl.notes || prev.notes,
      due_date: dueDate,
    }));
    toast.success(`Plantilla "${tpl.name}" aplicada`);
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_price);
    }, 0);
  };

  const calculateTax = () => {
    return formData.items.reduce((sum, item) => {
      const lineSubtotal = item.quantity * item.unit_price;
      return sum + (lineSubtotal * (item.tax_rate / 100));
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  // Extract field-specific errors from API
  const extractApiErrors = (e) => {
    const d = e?.response?.data?.detail;
    if (!d) return [{ field: '_', code: 'GENERIC', message: 'Error al guardar la factura.' }];
    if (typeof d === 'string') return [{ field: '_', code: 'GENERIC', message: d }];
    if (Array.isArray(d.errors)) return d.errors;
    if (d.message || d.code) return [{ field: d.field || '_', code: d.code || 'GENERIC', message: d.message || 'Error' }];
    return [{ field: '_', code: 'GENERIC', message: JSON.stringify(d) }];
  };

  const buildPayload = () => ({
    ...formData,
    series: (formData.series || 'FAC').toUpperCase(),
    operation_date: formData.operation_date || null,
    irpf_rate: parseFloat(formData.irpf_rate) || 0,
    items: formData.items.filter(item => item.description).map(item => ({
      ...item,
      quantity: parseFloat(item.quantity) || 0,
      unit_price: parseFloat(item.unit_price) || 0,
      tax_rate: parseFloat(item.tax_rate),
    })),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors([]);

    if (isLocked) {
      toast.error('La factura está emitida y es inmutable.');
      return;
    }
    if (!formData.client_id) {
      toast.error('Selecciona un cliente');
      return;
    }
    if (formData.items.every(item => !item.description)) {
      toast.error('Añade al menos un concepto');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEditing) {
        await updateInvoice(id, payload);
        toast.success('Borrador actualizado');
      } else {
        await createInvoice(payload);
        toast.success('Borrador creado');
      }
      navigate('/invoices');
    } catch (e) {
      const errs = extractApiErrors(e);
      setFieldErrors(errs);
      toast.error(errs[0]?.message || 'Error al guardar la factura');
    } finally {
      setSaving(false);
    }
  };

  const handleEmit = async () => {
    if (!isEditing) {
      toast.error('Primero guarda el borrador y luego emítelo.');
      return;
    }
    if (!window.confirm('¿Emitir esta factura? Una vez emitida no podrás modificarla. Solo podrás crear una factura rectificativa.')) return;

    setFieldErrors([]);
    setEmitting(true);
    try {
      // Save any pending changes to the draft first
      await updateInvoice(id, buildPayload());
      const { data } = await emitInvoice(id, {});
      toast.success(`Factura ${data.invoice_number} emitida correctamente.`);
      navigate('/invoices');
    } catch (e) {
      const errs = extractApiErrors(e);
      setFieldErrors(errs);
      toast.error(errs[0]?.message || 'No se pudo emitir la factura.');
    } finally {
      setEmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/invoices')}
          className="p-2 hover:bg-stone-100 rounded-xl transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">
            {isEditing ? 'Editar Factura' : 'Nueva Factura'}
          </h1>
          <p className="text-stone-500 text-sm mt-1">
            {isEditing ? 'Modifica los datos de la factura' : 'Crea una nueva factura'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <fieldset disabled={isLocked} className={isLocked ? 'opacity-95' : ''}>
        {/* Basic Info */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <h2 className="text-lg font-heading font-bold mb-4">Datos Básicos</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="client">Cliente *</Label>
                <Client360Link clientId={formData.client_id} testid="invoice-form-view-360" />
              </div>
              <Select
                value={formData.client_id || undefined}
                onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="client-select">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice_number">Número de Factura</Label>
              <Input
                id="invoice_number"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                className="rounded-xl font-mono"
                placeholder="Se genera automáticamente"
                data-testid="invoice-number-input"
              />
              <p className="text-xs text-stone-500">Déjalo vacío para generación automática</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="issue_date">Fecha de Emisión</Label>
              <Input
                id="issue_date"
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                className="rounded-xl"
                data-testid="issue-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="due_date">Fecha de Vencimiento</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="rounded-xl"
                data-testid="due-date-input"
              />
            </div>
          </div>
        </div>

        {/* Templates panel (visible only on create + if any template exists) */}
        {!isEditing && (contentTemplates.length > 0 || designTemplates.length > 0) && (
          <div className="bg-gradient-to-br from-violet-50 to-orange-50/40 rounded-3xl border border-violet-100 p-4 sm:p-6" data-testid="templates-panel">
            <div className="flex flex-col sm:flex-row gap-4">
              {contentTemplates.length > 0 && (
                <div className="flex-1 min-w-0">
                  <Label className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-[0.15em] font-bold text-stone-600">
                    <FileText className="w-3.5 h-3.5" />
                    Cargar plantilla de contenido
                  </Label>
                  <Select onValueChange={applyContentTemplate}>
                    <SelectTrigger className="rounded-xl bg-white" data-testid="apply-content-template">
                      <SelectValue placeholder="Selecciona una plantilla..." />
                    </SelectTrigger>
                    <SelectContent>
                      {contentTemplates.map(tpl => (
                        <SelectItem key={tpl.id} value={tpl.id}>
                          {tpl.name} · {tpl.items?.length || 0} concepto{(tpl.items?.length || 0) === 1 ? '' : 's'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {designTemplates.length > 0 && (
                <div className="flex-1 min-w-0">
                  <Label className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-[0.15em] font-bold text-stone-600">
                    <Palette className="w-3.5 h-3.5" />
                    Diseño del PDF
                  </Label>
                  <Select
                    value={formData.template_id || '__default__'}
                    onValueChange={(v) => setFormData({ ...formData, template_id: v === '__default__' ? '' : v })}
                  >
                    <SelectTrigger className="rounded-xl bg-white" data-testid="select-design-template">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">Plantilla por defecto</SelectItem>
                      {designTemplates.map(tpl => (
                        <SelectItem key={tpl.id} value={tpl.id}>
                          {tpl.name}{tpl.is_default ? ' ⭐' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Line Items */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
            <h2 className="text-lg font-heading font-bold">Conceptos</h2>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {services.length > 0 && (
                <Select onValueChange={addServiceToItems}>
                  <SelectTrigger className="w-full sm:w-48 rounded-xl" data-testid="add-service-select">
                    <SelectValue placeholder="Añadir servicio" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name} - {formatCurrency(service.hourly_rate)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {articles.length > 0 && (
                <Select onValueChange={addArticleToItems}>
                  <SelectTrigger className="w-full sm:w-48 rounded-xl" data-testid="add-article-select">
                    <SelectValue placeholder="Añadir artículo" />
                  </SelectTrigger>
                  <SelectContent>
                    {articles.map((article) => (
                      <SelectItem key={article.id} value={article.id}>
                        {article.sku ? `[${article.sku}] ` : ''}{article.name} - {formatCurrency(article.unit_price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Header - hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-4 text-xs uppercase tracking-[0.15em] font-bold text-stone-500 px-1">
              <div className="col-span-5">Descripción</div>
              <div className="col-span-2">Cantidad</div>
              <div className="col-span-2">Precio</div>
              <div className="col-span-2">IVA %</div>
              <div className="col-span-1"></div>
            </div>

            {/* Items */}
            {formData.items.map((item, index) => (
              <div key={index} className="border border-stone-100 rounded-xl p-3 md:p-0 md:border-0 md:rounded-none">
                {/* Desktop layout */}
                <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-5">
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      placeholder="Descripción del concepto"
                      className="rounded-xl"
                      data-testid={`item-description-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      className="rounded-xl font-mono"
                      data-testid={`item-quantity-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      className="rounded-xl font-mono"
                      data-testid={`item-price-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={item.tax_rate}
                      onChange={(e) => updateItem(index, 'tax_rate', e.target.value)}
                      className="rounded-xl font-mono"
                      data-testid={`item-tax-${index}`}
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-1.5 hover:bg-red-50 rounded-xl transition-colors"
                      disabled={formData.items.length === 1}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
                {/* Mobile layout */}
                <div className="md:hidden space-y-3">
                  <div className="flex justify-between items-start">
                    <Label className="text-xs text-stone-500">Concepto {index + 1}</Label>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-1 hover:bg-red-50 rounded-xl transition-colors"
                      disabled={formData.items.length === 1}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                  <Input
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="Descripción"
                    className="rounded-xl"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-stone-500">Cant.</Label>
                      <Input type="number" step="0.01" min="0" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Precio</Label>
                      <Input type="number" step="0.01" min="0" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">IVA %</Label>
                      <Input type="number" min="0" max="100" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addItem}
              className="rounded-xl"
              data-testid="add-item-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Añadir Concepto
            </Button>
          </div>

          {/* Totals */}
          <div className="mt-6 pt-6 border-t border-stone-100">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">Subtotal</span>
                  <span className="font-mono">{formatCurrency(calculateSubtotal())}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">IVA</span>
                  <span className="font-mono">{formatCurrency(calculateTax())}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-stone-100">
                  <span>Total</span>
                  <span className="font-mono text-[#FF6B4A]" data-testid="invoice-total">
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <h2 className="text-lg font-heading font-bold mb-4">Notas</h2>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Notas adicionales para la factura..."
            className="rounded-xl resize-none"
            rows={3}
            data-testid="invoice-notes-input"
          />

          {/* Stock auto-decrement option, only visible when invoice has at least one article */}
          {!isEditing && formData.items.some(it => it.article_id) && (
            <label className="flex items-start gap-3 mt-4 p-3 bg-violet-50 border border-violet-100 rounded-xl cursor-pointer hover:bg-violet-100/60 transition-colors" data-testid="auto-stock-toggle">
              <input
                type="checkbox"
                checked={formData.auto_stock}
                onChange={(e) => setFormData({ ...formData, auto_stock: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-violet-600"
              />
              <div className="text-sm">
                <span className="font-medium text-violet-900">Descontar stock automáticamente</span>
                <p className="text-xs text-violet-700/80 mt-0.5">Se restará la cantidad vendida del stock de cada artículo con control activado. Al anular la factura, el stock se repondrá.</p>
              </div>
            </label>
          )}
        </div>

        </fieldset>

        {/* Locked banner */}
        {isLocked && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="locked-banner">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">Factura emitida — inmutable</p>
              <p className="text-sm text-amber-800/90 mt-0.5">
                Esta factura ya fue emitida y, por normativa Verifactu, no puede modificarse ni eliminarse.
                Para corregirla, vuelve al listado y crea una factura rectificativa.
              </p>
            </div>
          </div>
        )}

        {/* Field errors summary */}
        {fieldErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-2" data-testid="field-errors-banner">
            <div className="flex items-center gap-2 font-semibold text-red-900">
              <AlertCircle className="w-4 h-4" />
              No se pudo {emitting ? 'emitir' : 'guardar'} la factura
            </div>
            <ul className="space-y-1 text-sm text-red-800">
              {fieldErrors.map((err, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`field-error-${err.code || i}`}>
                  <span className="font-mono text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded mt-0.5">{err.field || 'general'}</span>
                  <span>{err.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pb-6 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/invoices')}
            className="rounded-xl"
            data-testid="cancel-button"
          >
            Cancelar
          </Button>
          {!isLocked && (
            <Button
              type="submit"
              disabled={saving || emitting}
              variant="outline"
              className="rounded-xl"
              data-testid="save-draft-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando…' : (isEditing ? 'Guardar borrador' : 'Crear borrador')}
            </Button>
          )}
          {isEditing && !isLocked && (
            <Button
              type="button"
              onClick={handleEmit}
              disabled={emitting || saving}
              className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
              data-testid="emit-invoice-button"
            >
              <Send className="w-4 h-4 mr-2" />
              {emitting ? 'Emitiendo…' : 'Emitir factura'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default InvoiceFormPage;
